// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Tables Floor
// Grid of all available table games (Blackjack, Roulette, etc.)
// ================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCasino } from '../context/CasinoShell';

const TABLE_GAMES = [
  {
    id: 'blackjack',
    title: 'Blackjack',
    icon: '🃏',
    description: 'Classic 21 with dealer AI. Hit, Stand, Double Down. Blackjack pays 3:2.',
    minBet: 1,
    maxBet: 100,
    houseEdge: '0.5%',
    features: ['Provably Fair', 'Double Down', 'Natural 3:2'],
    color: '#22C55E',
    tag: 'POPULAR',
    status: 'live',
  },
  {
    id: 'roulette',
    title: 'Roulette',
    icon: '🎯',
    description: 'American roulette with animated wheel. Inside & outside bets. Straight up pays 35:1.',
    minBet: 1,
    maxBet: 500,
    houseEdge: '5.26%',
    features: ['Animated Wheel', 'Multiple Bet Types', '35:1 Straight'],
    color: '#DC2626',
    tag: 'CLASSIC',
    status: 'live',
  },
  {
    id: 'video-poker',
    title: 'Video Poker',
    icon: '🎴',
    description: 'Jacks or Better. 5-card draw with hold/discard. Royal Flush pays 250x.',
    minBet: 1,
    maxBet: 25,
    houseEdge: '0.46%',
    features: ['Jacks or Better', 'Hold/Draw', '250x Royal Flush'],
    color: '#7C3AED',
    tag: 'BEST ODDS',
    status: 'live',
  },
  {
    id: 'craps',
    title: 'Craps',
    icon: '🎲',
    description: 'Full craps with Pass, Don\'t Pass, Field, Any 7, and Craps bets. Animated dice.',
    minBet: 1,
    maxBet: 50,
    houseEdge: '1.41%',
    features: ['Pass/Don\'t Pass', 'Field Bets', 'Prop Bets'],
    color: '#EAB308',
    tag: 'NEW',
    status: 'live',
  },
];

export default function TablesFloor() {
  const navigate = useNavigate();
  const { forgeBalance } = useCasino();

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{'🃏'} TABLE GAMES</h1>
          <p style={styles.subtitle}>{TABLE_GAMES.length} provably fair table games</p>
        </div>
        <div style={styles.balanceBadge}>
          <span style={styles.balanceLabel}>BALANCE</span>
          <span style={styles.balanceValue}>${forgeBalance.toFixed(2)}</span>
        </div>
      </div>

      {/* Game List */}
      <div style={styles.gameList}>
        {TABLE_GAMES.map(game => (
          <div key={game.id} style={styles.gameCard}>
            <div style={styles.cardHeader}>
              <span style={styles.gameIcon}>{game.icon}</span>
              <div style={styles.cardHeaderText}>
                <div style={styles.titleRow}>
                  <h3 style={{ ...styles.gameTitle, color: game.color }}>{game.title}</h3>
                  {game.tag && <span style={{ ...styles.tag, background: game.color + '22', color: game.color, borderColor: game.color + '44' }}>{game.tag}</span>}
                </div>
                <p style={styles.gameDesc}>{game.description}</p>
              </div>
            </div>

            <div style={styles.specGrid}>
              <div style={styles.spec}>
                <span style={styles.specLabel}>MIN BET</span>
                <span style={styles.specValue}>${game.minBet}</span>
              </div>
              <div style={styles.spec}>
                <span style={styles.specLabel}>MAX BET</span>
                <span style={styles.specValue}>${game.maxBet}</span>
              </div>
              <div style={styles.spec}>
                <span style={styles.specLabel}>HOUSE EDGE</span>
                <span style={{ ...styles.specValue, color: '#22C55E' }}>{game.houseEdge}</span>
              </div>
            </div>

            <div style={styles.features}>
              {game.features.map(f => (
                <span key={f} style={{ ...styles.featureTag, background: game.color + '15', borderColor: game.color + '33', color: game.color }}>{f}</span>
              ))}
            </div>

            <button
              style={{ ...styles.playBtn, borderColor: game.color, background: `linear-gradient(180deg, ${game.color} 0%, ${game.color}CC 100%)` }}
              onClick={() => navigate(`/tables/${game.id}`)}
            >
              PLAY {game.title.toUpperCase()}
            </button>
          </div>
        ))}
      </div>

      {/* Fair play footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          All table games use SHA-256 provably fair technology. Every deal, roll, and spin is cryptographically verified.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 700, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 16, flexWrap: 'wrap', gap: 12,
  },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, margin: 0 },
  subtitle: { fontSize: 11, color: '#666', marginTop: 2 },
  balanceBadge: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
    padding: '8px 14px', background: '#111', borderRadius: 8, border: '1px solid #222',
  },
  balanceLabel: { fontSize: 8, color: '#555', fontWeight: 700, letterSpacing: 1 },
  balanceValue: { fontSize: 18, fontWeight: 900, color: '#FFD700', fontFamily: "'Courier New', monospace" },
  gameList: { display: 'flex', flexDirection: 'column', gap: 16 },
  gameCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222' },
  cardHeader: { display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 },
  cardHeaderText: { flex: 1 },
  gameIcon: { fontSize: 36 },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8 },
  gameTitle: { fontSize: 16, fontWeight: 900, letterSpacing: 1, margin: 0 },
  tag: {
    padding: '2px 8px', borderRadius: 4, border: '1px solid',
    fontSize: 8, fontWeight: 900, letterSpacing: 1,
  },
  gameDesc: { fontSize: 11, color: '#888', lineHeight: 1.5, marginTop: 4 },
  specGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 },
  spec: {
    padding: '6px 8px', background: '#0a0a0a', borderRadius: 6,
    border: '1px solid #1a1a1a', textAlign: 'center',
  },
  specLabel: { display: 'block', fontSize: 7, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 2 },
  specValue: { fontSize: 12, fontWeight: 900, color: '#fff' },
  features: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  featureTag: {
    padding: '3px 8px', borderRadius: 4, border: '1px solid',
    fontSize: 9, fontWeight: 700,
  },
  playBtn: {
    width: '100%', padding: 12, borderRadius: 8,
    border: '2px solid', color: '#fff',
    fontSize: 13, fontWeight: 900, letterSpacing: 2,
    cursor: 'pointer', boxShadow: '0 0 12px rgba(0,0,0,0.3)',
  },
  footer: { textAlign: 'center', padding: '20px 0', marginTop: 20 },
  footerText: { fontSize: 10, color: '#444', lineHeight: 1.5 },
};
